import {
  BoxGeometry,
  BufferGeometry,
  CylinderGeometry,
  Group,
  type Material,
  Mesh,
} from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";

import type { CityMaterialLibrary } from "../materials/cityMaterials.ts";
import type {
  TileBusStopSource,
  VilniusStopFurnitureProfile,
} from "../../world/busStops.ts";

export interface BusStopFurniturePlacement {
  readonly source: Readonly<TileBusStopSource>;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly yaw: number;
}

export type BusStopGroundHeightSampler = (
  x: number,
  z: number,
) => number | null;

type LocalGroundOffsetSampler = (
  localX: number,
  localZ: number,
) => number;

interface ShelterDimensions {
  readonly widthM: number;
  readonly depthM: number;
  readonly heightM: number;
  readonly glazingModules: number;
  readonly curvedCanopy: boolean;
}

type GeometryBucketName =
  | "frame"
  | "blue"
  | "glass"
  | "ivory"
  | "seat"
  | "light"
  | "tactile";

interface GeometryBucket {
  readonly material: Material;
  readonly geometries: BufferGeometry[];
}

interface LocalSupportPoint {
  readonly x: number;
  readonly z: number;
}

const FURNITURE_GROUND_CLEARANCE_M = 0.012;

/**
 * Vilnius' street standard specifies a 6 x 2 m functional public-transport
 * pavilion module with dark RAL 7022/9004 framing. Smaller profiles are used
 * only where the matched OSM record explicitly describes reduced furniture.
 */
export const BUS_STOP_SHELTER_DIMENSIONS: Readonly<
  Record<
    Exclude<VilniusStopFurnitureProfile, "bench-only" | "historic-pole">,
    Readonly<ShelterDimensions>
  >
> = Object.freeze({
  "glass-shelter-display": Object.freeze({
    widthM: 6,
    depthM: 2,
    heightM: 2.72,
    glazingModules: 3,
    curvedCanopy: false,
  }),
  "glass-shelter": Object.freeze({
    widthM: 6,
    depthM: 2,
    heightM: 2.72,
    glazingModules: 3,
    curvedCanopy: false,
  }),
  "shelter-no-bench": Object.freeze({
    widthM: 4.2,
    depthM: 1.82,
    heightM: 2.62,
    glazingModules: 2,
    curvedCanopy: false,
  }),
  "airport-shelter": Object.freeze({
    widthM: 6.6,
    depthM: 2.35,
    heightM: 2.86,
    glazingModules: 3,
    curvedCanopy: true,
  }),
});

function transformed(
  geometry: BufferGeometry,
  position: readonly [number, number, number],
  rotation: readonly [number, number, number] = [0, 0, 0],
): BufferGeometry {
  geometry.rotateX(rotation[0]);
  geometry.rotateY(rotation[1]);
  geometry.rotateZ(rotation[2]);
  geometry.translate(...position);
  return geometry;
}

function addBox(
  buckets: Readonly<Record<GeometryBucketName, GeometryBucket>>,
  bucket: GeometryBucketName,
  size: readonly [number, number, number],
  position: readonly [number, number, number],
  rotation: readonly [number, number, number] = [0, 0, 0],
): void {
  buckets[bucket].geometries.push(transformed(
    new BoxGeometry(...size),
    position,
    rotation,
  ));
}

function addCylinder(
  buckets: Readonly<Record<GeometryBucketName, GeometryBucket>>,
  bucket: GeometryBucketName,
  radiusTopM: number,
  radiusBottomM: number,
  heightM: number,
  position: readonly [number, number, number],
): void {
  buckets[bucket].geometries.push(transformed(
    new CylinderGeometry(radiusTopM, radiusBottomM, heightM, 10),
    position,
  ));
}

function worldPointForLocal(
  placement: Readonly<BusStopFurniturePlacement>,
  localX: number,
  localZ: number,
): Readonly<{ x: number; z: number }> {
  const cosine = Math.cos(placement.yaw);
  const sine = Math.sin(placement.yaw);
  return {
    x: placement.x + cosine * localX + sine * localZ,
    z: placement.z - sine * localX + cosine * localZ,
  };
}

function sampledGroundHeight(
  placement: Readonly<BusStopFurniturePlacement>,
  sampleGroundHeight: BusStopGroundHeightSampler | undefined,
  point: Readonly<LocalSupportPoint>,
): number {
  if (sampleGroundHeight === undefined) {
    return placement.y - FURNITURE_GROUND_CLEARANCE_M;
  }
  const world = worldPointForLocal(placement, point.x, point.z);
  return sampleGroundHeight(world.x, world.z) ??
    placement.y - FURNITURE_GROUND_CLEARANCE_M;
}

function addAdjustableFooting(
  buckets: Readonly<Record<GeometryBucketName, GeometryBucket>>,
  x: number,
  z: number,
  groundOffsetM: number,
  topY = 0.065,
): void {
  const heightM = Math.max(0.035, topY - groundOffsetM);
  const bottomY = topY - heightM;
  addCylinder(
    buckets,
    "frame",
    0.055,
    0.065,
    heightM,
    [x, bottomY + heightM / 2, z],
  );
  addBox(
    buckets,
    "frame",
    [0.2, 0.025, 0.2],
    [x, groundOffsetM + 0.0125, z],
  );
}

function mergedMesh(
  name: string,
  bucket: Readonly<GeometryBucket>,
  castsShadow: boolean,
): Mesh | null {
  if (bucket.geometries.length === 0) {
    return null;
  }
  const geometry = mergeGeometries(bucket.geometries);
  for (const owned of bucket.geometries) {
    owned.dispose();
  }
  if (geometry === null) {
    throw new Error(`Could not merge ${name}`);
  }
  geometry.name = name;
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  const mesh = new Mesh(geometry, bucket.material);
  mesh.name = name;
  mesh.castShadow = castsShadow;
  mesh.receiveShadow = true;
  return mesh;
}

function addShelterShell(
  buckets: Readonly<Record<GeometryBucketName, GeometryBucket>>,
  dimensions: Readonly<ShelterDimensions>,
  centerX: number,
  groundOffsetAt: LocalGroundOffsetSampler,
  components: string[],
): void {
  const { widthM, depthM, heightM, glazingModules, curvedCanopy } = dimensions;
  const rearZ = -depthM + 0.075;
  const frontZ = -0.075;
  const glazingHeightM = heightM - 0.48;
  const glazingCenterY = 0.16 + glazingHeightM / 2;
  const moduleWidthM = widthM / glazingModules;

  if (curvedCanopy) {
    const panelDepthM = depthM / 3 + 0.08;
    for (const [index, rotationX] of [0.12, 0, -0.12].entries()) {
      const z = -depthM + panelDepthM / 2 + index * (depthM / 3);
      const y = heightM + (index === 1 ? 0.08 : 0.015);
      addBox(
        buckets,
        "blue",
        [widthM + 0.22, 0.11, panelDepthM],
        [centerX, y, z],
        [rotationX, 0, 0],
      );
    }
    for (let index = 0; index <= glazingModules; index += 1) {
      const x = centerX - widthM / 2 + moduleWidthM * index;
      addBox(
        buckets,
        "frame",
        [0.065, 0.085, depthM + 0.14],
        [x, heightM - 0.015, -depthM / 2],
      );
    }
    components.push("segmented-airport-canopy", "canopy-ribs");
  } else {
    addBox(
      buckets,
      "frame",
      [widthM + 0.18, 0.14, depthM + 0.18],
      [centerX, heightM, -depthM / 2],
    );
    for (let index = 0; index <= glazingModules; index += 1) {
      const x = centerX - widthM / 2 + moduleWidthM * index;
      addBox(
        buckets,
        "frame",
        [0.055, 0.065, depthM + 0.22],
        [x, heightM - 0.105, -depthM / 2],
      );
    }
    components.push("flat-weather-canopy", "canopy-ribs");
  }

  addBox(
    buckets,
    "blue",
    [widthM + 0.22, 0.24, 0.12],
    [centerX, heightM - 0.09, frontZ],
  );
  addBox(
    buckets,
    "ivory",
    [widthM + 0.06, 0.055, 0.14],
    [centerX, heightM - 0.235, frontZ + 0.015],
  );
  addBox(
    buckets,
    "frame",
    [widthM, 0.13, 0.1],
    [centerX, 0.16, rearZ],
  );
  addBox(
    buckets,
    "frame",
    [widthM + 0.24, 0.075, 0.12],
    [centerX, heightM - 0.015, rearZ - 0.075],
  );
  addBox(
    buckets,
    "frame",
    [widthM + 0.24, 0.085, 0.1],
    [centerX, heightM - 0.015, frontZ + 0.06],
  );

  const leftX = centerX - widthM / 2 + 0.07;
  const rightX = centerX + widthM / 2 - 0.07;
  for (const x of [leftX, rightX]) {
    for (const z of [rearZ, frontZ]) {
      addBox(
        buckets,
        "frame",
        [0.11, heightM - 0.16, 0.11],
        [x, (heightM - 0.16) / 2, z],
      );
      addBox(buckets, "frame", [0.25, 0.045, 0.25], [x, 0.03, z]);
      addAdjustableFooting(buckets, x, z, groundOffsetAt(x, z));
    }
  }

  for (let index = 0; index < glazingModules; index += 1) {
    const x = centerX - widthM / 2 + moduleWidthM * (index + 0.5);
    addBox(
      buckets,
      "glass",
      [moduleWidthM - 0.13, glazingHeightM, 0.035],
      [x, glazingCenterY, rearZ],
    );
    addBox(
      buckets,
      "ivory",
      [moduleWidthM - 0.28, 0.045, 0.018],
      [x, 1.12, rearZ + 0.031],
    );
    for (const dotOffsetX of [-0.34, 0, 0.34]) {
      if (Math.abs(dotOffsetX) < moduleWidthM / 2 - 0.12) {
        addBox(
          buckets,
          "ivory",
          [0.045, 0.045, 0.019],
          [x + dotOffsetX, 1.2, rearZ + 0.032],
        );
      }
    }
    if (index > 0) {
      const mullionX = centerX - widthM / 2 + moduleWidthM * index;
      addBox(
        buckets,
        "frame",
        [0.075, glazingHeightM, 0.075],
        [mullionX, glazingCenterY, rearZ],
      );
    }
  }

  for (const x of [leftX, rightX]) {
    addBox(
      buckets,
      "glass",
      [0.035, glazingHeightM, depthM - 0.24],
      [x, glazingCenterY, -depthM / 2],
    );
    addBox(
      buckets,
      "ivory",
      [0.018, 0.045, depthM - 0.42],
      [x + (x === leftX ? 0.031 : -0.031), 1.12, -depthM / 2],
    );
    addBox(
      buckets,
      "frame",
      [0.085, 0.11, depthM - 0.16],
      [x, 0.18, -depthM / 2],
    );
  }
  addBox(
    buckets,
    "frame",
    [0.075, heightM - 0.3, 0.075],
    [rightX - 0.13, (heightM - 0.3) / 2, rearZ - 0.12],
  );
  addBox(
    buckets,
    "frame",
    [0.16, 0.07, 0.13],
    [rightX - 0.13, 0.09, rearZ - 0.08],
  );
  components.push(
    "segmented-rear-glazing",
    "glazed-wind-screens",
    "dark-frame",
    "reflective-fascia-trim",
    "canopy-gutters",
    "rainwater-downpipe",
    "glazing-safety-band",
    "anchored-base-shoes",
    "terrain-levelled-adjustable-footings",
  );
}

function benchWidthFor(widthM: number): number {
  return Math.min(widthM - 1.2, widthM >= 6.4 ? 4.5 : 3.7);
}

function addBench(
  buckets: Readonly<Record<GeometryBucketName, GeometryBucket>>,
  centerX: number,
  widthM: number,
  rearZ: number,
  groundOffsetAt: LocalGroundOffsetSampler,
  components: string[],
): void {
  const benchWidthM = benchWidthFor(widthM);
  const slatDepthM = 0.12;
  for (let index = 0; index < 4; index += 1) {
    addBox(
      buckets,
      "seat",
      [benchWidthM, 0.09, slatDepthM],
      [centerX, 0.58, rearZ + 0.35 + index * 0.13],
    );
  }
  for (let index = 0; index < 4; index += 1) {
    addBox(
      buckets,
      "seat",
      [benchWidthM, 0.1, 0.11],
      [centerX, 0.79 + index * 0.17, rearZ + 0.17],
      [-0.08, 0, 0],
    );
  }
  for (const x of [centerX - benchWidthM * 0.36, centerX + benchWidthM * 0.36]) {
    addBox(buckets, "frame", [0.11, 0.57, 0.11], [x, 0.285, rearZ + 0.5]);
    addBox(buckets, "frame", [0.11, 0.88, 0.11], [x, 0.44, rearZ + 0.15]);
    addBox(buckets, "frame", [0.28, 0.035, 0.23], [x, 0.025, rearZ + 0.44]);
    addAdjustableFooting(
      buckets,
      x,
      rearZ + 0.44,
      groundOffsetAt(x, rearZ + 0.44),
      0.05,
    );
  }
  for (const x of [
    centerX - benchWidthM / 2 + 0.18,
    centerX,
    centerX + benchWidthM / 2 - 0.18,
  ]) {
    addBox(buckets, "frame", [0.055, 0.33, 0.055], [x, 0.745, rearZ + 0.52]);
    addBox(buckets, "frame", [0.055, 0.055, 0.43], [x, 0.9, rearZ + 0.33]);
  }
  components.push(
    "slatted-bench",
    "bench-armrests",
    "bench-anchor-feet",
    "terrain-levelled-bench-feet",
  );
}

function addBin(
  buckets: Readonly<Record<GeometryBucketName, GeometryBucket>>,
  x: number,
  z: number,
  groundOffsetAt: LocalGroundOffsetSampler,
  components: string[],
): void {
  addCylinder(buckets, "frame", 0.24, 0.29, 0.78, [x, 0.39, z]);
  addCylinder(buckets, "blue", 0.26, 0.26, 0.07, [x, 0.795, z]);
  addCylinder(buckets, "frame", 0.3, 0.3, 0.045, [x, 0.0225, z]);
  addBox(buckets, "ivory", [0.24, 0.12, 0.025], [x, 0.64, z + 0.245]);
  addBox(buckets, "blue", [0.09, 0.035, 0.04], [x, 0.09, z + 0.29]);
  addAdjustableFooting(buckets, x, z, groundOffsetAt(x, z), 0.045);
  components.push(
    "lidded-bin",
    "bin-opening",
    "bin-foot-pedal",
    "terrain-levelled-bin-base",
  );
}

function addTactilePaving(
  buckets: Readonly<Record<GeometryBucketName, GeometryBucket>>,
  centerX: number,
  widthM: number,
  groundOffsetAt: LocalGroundOffsetSampler,
  components: string[],
): void {
  const stripWidthM = Math.min(widthM, 5.4);
  const columns = Math.max(3, Math.floor(stripWidthM / 0.2));
  const spacingX = stripWidthM / columns;
  for (let column = 0; column < columns; column += 1) {
    const x = centerX - stripWidthM / 2 + spacingX * (column + 0.5);
    const groundOffsetM = groundOffsetAt(x, 0.52);
    addBox(
      buckets,
      "tactile",
      [spacingX * 0.97, 0.025, 0.54],
      [x, groundOffsetM + 0.0125, 0.52],
    );
  }
  for (let row = 0; row < 3; row += 1) {
    for (let column = 0; column < columns; column += 1) {
      const x = centerX - stripWidthM / 2 + spacingX * (column + 0.5);
      const z = 0.34 + row * 0.18;
      const groundOffsetM = groundOffsetAt(x, z);
      addCylinder(
        buckets,
        "tactile",
        0.032,
        0.038,
        0.022,
        [
          x,
          groundOffsetM + 0.036,
          z,
        ],
      );
    }
  }
  components.push(
    "tactile-warning-strip",
    "raised-tactile-studs",
    "terrain-conforming-tactile-sections",
  );
}

function addRealtimeDisplay(
  buckets: Readonly<Record<GeometryBucketName, GeometryBucket>>,
  x: number,
  rearZ: number,
  groundOffsetAt: LocalGroundOffsetSampler,
  components: string[],
): void {
  for (const offsetX of [-0.56, 0.56]) {
    addBox(
      buckets,
      "frame",
      [0.1, 1.82, 0.1],
      [x + offsetX, 0.91, rearZ + 0.09],
    );
    addAdjustableFooting(
      buckets,
      x + offsetX,
      rearZ + 0.09,
      groundOffsetAt(x + offsetX, rearZ + 0.09),
    );
  }
  addBox(buckets, "blue", [1.26, 0.92, 0.11], [x, 1.67, rearZ + 0.115]);
  addBox(buckets, "frame", [1.08, 0.7, 0.025], [x, 1.67, rearZ + 0.18]);
  addBox(buckets, "ivory", [1.0, 0.08, 0.02], [x, 1.98, rearZ + 0.201]);
  for (let index = 0; index < 4; index += 1) {
    const rowY = 1.87 - index * 0.15;
    addBox(
      buckets,
      "ivory",
      [0.11, 0.075, 0.019],
      [x - 0.42, rowY, rearZ + 0.201],
    );
    addBox(
      buckets,
      "light",
      [0.53 - index * 0.045, 0.035, 0.018],
      [x - 0.08 + index * 0.02, rowY, rearZ + 0.203],
    );
    addBox(
      buckets,
      "light",
      [0.12, 0.045, 0.018],
      [x + 0.39, rowY, rearZ + 0.203],
    );
  }
  for (let divider = 0; divider < 3; divider += 1) {
    addBox(
      buckets,
      "frame",
      [0.96, 0.012, 0.018],
      [x, 1.795 - divider * 0.15, rearZ + 0.202],
    );
  }
  addBox(
    buckets,
    "blue",
    [1.04, 0.055, 0.54],
    [x, 2.38, rearZ + 0.08],
    [0.17, 0, 0],
  );
  components.push(
    "realtime-display",
    "display-route-rows",
    "display-time-column",
    "solar-display-cap",
    "terrain-levelled-display-feet",
  );
}

function addHistoricPoleDetails(
  buckets: Readonly<Record<GeometryBucketName, GeometryBucket>>,
  components: string[],
): void {
  addCylinder(buckets, "frame", 0.19, 0.24, 0.2, [0, 0.1, 0]);
  addCylinder(buckets, "frame", 0.11, 0.16, 0.16, [0, 0.27, 0]);
  for (const y of [0.36, 1.45, 2.42, 3.43]) {
    addCylinder(buckets, "frame", 0.085, 0.085, 0.055, [0, y, 0]);
  }
  addCylinder(buckets, "ivory", 0.095, 0.095, 0.025, [0, 2.42, 0]);
  addCylinder(buckets, "frame", 0, 0.13, 0.22, [0, 3.59, 0]);
  components.push("old-town-cast-collars", "faceted-pole-finial");
}

function createBuckets(
  materials: CityMaterialLibrary,
): Readonly<Record<GeometryBucketName, GeometryBucket>> {
  return {
    frame: { material: materials.streetPole, geometries: [] },
    blue: { material: materials.busStopBlue, geometries: [] },
    glass: { material: materials.busStopGlass, geometries: [] },
    ivory: { material: materials.busStopIvory, geometries: [] },
    seat: { material: materials.busStopSeat, geometries: [] },
    light: { material: materials.lampBulb, geometries: [] },
    tactile: { material: materials.busStopTactile, geometries: [] },
  };
}

export function createEvidenceBackedBusStopFurniture(
  placement: Readonly<BusStopFurniturePlacement>,
  materials: CityMaterialLibrary,
  sampleGroundHeight?: BusStopGroundHeightSampler,
): Group | null {
  const evidence = placement.source.furniture;
  if (evidence === null) {
    return null;
  }
  const group = new Group();
  group.name = `vilnius-stop-furniture:${placement.source.id}`;
  group.rotation.y = placement.yaw;
  const buckets = createBuckets(materials);
  const components: string[] = [];
  const dimensions = evidence.shelter
    ? BUS_STOP_SHELTER_DIMENSIONS[
      evidence.profile as keyof typeof BUS_STOP_SHELTER_DIMENSIONS
    ]
    : null;
  const shelterCenterX = dimensions === null
    ? -1.55
    : -(dimensions.widthM / 2 + 0.48);
  const rearZ = dimensions === null ? -0.8 : -dimensions.depthM + 0.075;
  const frontZ = -0.075;
  const furnitureWidthM = dimensions?.widthM ?? 3.8;
  const benchWidthM = benchWidthFor(furnitureWidthM);
  const binX = dimensions === null
    ? shelterCenterX - 2.25
    : shelterCenterX - dimensions.widthM / 2 + 0.42;
  const binZ = rearZ + 0.45;
  const displayX = dimensions === null
    ? shelterCenterX
    : shelterCenterX + dimensions.widthM / 2 - 0.88;
  const supportPoints: LocalSupportPoint[] = [];
  if (dimensions !== null) {
    const leftX = shelterCenterX - dimensions.widthM / 2 + 0.07;
    const rightX = shelterCenterX + dimensions.widthM / 2 - 0.07;
    for (const x of [leftX, rightX]) {
      for (const z of [rearZ, frontZ]) {
        supportPoints.push({ x, z });
      }
    }
  }
  if (evidence.bench) {
    for (const x of [
      shelterCenterX - benchWidthM * 0.36,
      shelterCenterX + benchWidthM * 0.36,
    ]) {
      supportPoints.push({ x, z: rearZ + 0.44 });
    }
  }
  if (evidence.bin) {
    supportPoints.push({ x: binX, z: binZ });
  }
  if (evidence.passengerInformationDisplay) {
    for (const offsetX of [-0.56, 0.56]) {
      supportPoints.push({ x: displayX + offsetX, z: rearZ + 0.09 });
    }
  }
  const supportGroundHeights = supportPoints.map((point) =>
    sampledGroundHeight(placement, sampleGroundHeight, point)
  );
  const minimumSupportElevationM = supportGroundHeights.length === 0
    ? placement.y - FURNITURE_GROUND_CLEARANCE_M
    : Math.min(...supportGroundHeights);
  const maximumSupportElevationM = supportGroundHeights.length === 0
    ? placement.y - FURNITURE_GROUND_CLEARANCE_M
    : Math.max(...supportGroundHeights);
  const furnitureBaseY = supportGroundHeights.length === 0
    ? placement.y
    : Math.max(
        placement.y,
        ...supportGroundHeights.map((heightM) =>
          heightM + FURNITURE_GROUND_CLEARANCE_M
        ),
      );
  group.position.set(placement.x, furnitureBaseY, placement.z);
  const groundOffsetAt: LocalGroundOffsetSampler = (localX, localZ) =>
    sampledGroundHeight(
      placement,
      sampleGroundHeight,
      { x: localX, z: localZ },
    ) - furnitureBaseY;

  if (dimensions !== null) {
    addShelterShell(
      buckets,
      dimensions,
      shelterCenterX,
      groundOffsetAt,
      components,
    );
  }
  if (evidence.profile === "historic-pole") {
    addHistoricPoleDetails(buckets, components);
  }
  if (evidence.bench) {
    addBench(
      buckets,
      shelterCenterX,
      furnitureWidthM,
      rearZ,
      groundOffsetAt,
      components,
    );
  }
  if (evidence.bin) {
    addBin(buckets, binX, binZ, groundOffsetAt, components);
  }
  if (evidence.tactilePaving) {
    addTactilePaving(
      buckets,
      shelterCenterX,
      dimensions?.widthM ?? 4.2,
      groundOffsetAt,
      components,
    );
  }
  if (evidence.passengerInformationDisplay) {
    addRealtimeDisplay(
      buckets,
      displayX,
      rearZ,
      groundOffsetAt,
      components,
    );
  }
  if (evidence.lit && dimensions !== null) {
    for (const offsetX of [-dimensions.widthM * 0.22, dimensions.widthM * 0.22]) {
      addBox(
        buckets,
        "light",
        [1.15, 0.035, 0.11],
        [shelterCenterX + offsetX, dimensions.heightM - 0.13, -dimensions.depthM * 0.48],
      );
    }
    components.push("ceiling-lights");
  }

  for (const [name, bucket] of Object.entries(buckets) as [
    GeometryBucketName,
    GeometryBucket,
  ][]) {
    const mesh = mergedMesh(
      `${group.name}:${name}`,
      bucket,
      name !== "glass" && name !== "light" && name !== "tactile",
    );
    if (mesh !== null) {
      group.add(mesh);
    }
  }

  group.userData = {
    stopId: placement.source.id,
    stopName: placement.source.name,
    profile: evidence.profile,
    representation: dimensions === null
      ? evidence.bench
        ? "evidence-backed-open-bench-and-stop-sign"
        : "evidence-backed-stop-sign-only"
      : "evidence-backed-vilnius-dark-frame-glass-pavilion",
    sourceLayer: "openstreetmap-platform-tags",
    osmNodeId: evidence.osmNodeId,
    sourceDistanceM: evidence.distanceM,
    components,
    grounding: {
      strategy: sampleGroundHeight === undefined
        ? "pole-height-fallback"
        : "level-structure-with-terrain-sampled-adjustable-footings",
      sampledSupportCount: supportPoints.length,
      baseElevationM: furnitureBaseY,
      minimumSupportElevationM,
      maximumSupportElevationM,
      terrainSpanM: maximumSupportElevationM - minimumSupportElevationM,
      maximumFootingExtensionM:
        furnitureBaseY - minimumSupportElevationM,
    },
    shelter: dimensions === null
      ? null
      : {
          widthM: dimensions.widthM,
          depthM: dimensions.depthM,
          heightM: dimensions.heightM,
          glazingModules: dimensions.glazingModules,
          anchor: "stop-pole-at-curbward-edge-shelter-behind",
        },
    observed: {
      shelter: evidence.shelter,
      bench: evidence.bench,
      bin: evidence.bin,
      tactilePaving: evidence.tactilePaving,
      passengerInformationDisplay: evidence.passengerInformationDisplay,
      lit: evidence.lit,
    },
  };
  return group;
}
